import type { WorkflowGraph } from '../../graph';
import { useGraphEditorStore } from '../graphEditorStore';

export function wireGraphToStore(graph: WorkflowGraph): () => void {
  useGraphEditorStore.getState()._registerGraphGetter(() => graph);

  const unsubscribe = graph.on((event) => {
    const state = useGraphEditorStore.getState();

    if (state.sync.syncInProgress) return;

    switch (event.kind) {
      case 'selection-changed':
        state._applyGraphSelectionChange(event.cellIds);
        break;

      case 'cells-removed':
        {
          const removableIds = event.cellIds.filter((id) =>
            id in state.graphData.materialNodes ||
            id in state.graphData.operationNodes ||
            id in state.graphData.flowEdges ||
            id in state.graphData.groups,
          );

          if (removableIds.length > 0) {
            state._applyGraphCellsRemoved(removableIds);
          }
        }
        break;

      case 'cells-folded':
        // User clicked the swimlane fold button — sync collapsed state to store
        // so that useVisualGraphSync doesn't fight the user's action.
        for (const { id, isCollapsed } of event.foldedGroups) {
          state._applyGroupCollapsed(id, isCollapsed);
        }
        break;

      case 'undo':
        // maxGraph's UndoManager fired (e.g. keyboard plugin Ctrl+Z).
        // In our architecture, undo/redo is owned by the Zustand store —
        // flow edges only exist in the store and graph.toSnapshot() would
        // return visual-edge IDs (ve-1, ve-2, …), not the real edge IDs.
        // Delegate to the store's undo instead.
        state.undo();
        break;

      case 'redo':
        state.redo();
        break;

      case 'model-changed':
      case 'cells-inserted':
        // Not handled from graph → store in this phase
        break;
    }
  });

  return () => {
    unsubscribe();
    useGraphEditorStore.getState()._registerGraphGetter(() => null);
  };
}
